import { isPartStatusCancelled, isPartStatusError, type PartStatusLike } from '#plugin-chat/hooks/isErroredToolPart.ts';

/**
 * Whether a message part is a tool call currently **awaiting user input** —
 * i.e. a `requires-action` interrupt (the state `askUserQuestion` enters
 * while its `human()` callback is unresolved).
 *
 * Detected from either source the runtime may use: the computed part status
 * (`type: 'requires-action'`, `reason: 'interrupt'`) or a populated
 * `interrupt` field on a tool-call part. Mirrors the structural-type,
 * hook-free predicate pattern of `isErroredToolPart`; it is the SSOT for the
 * per-part `usePartStatus().isAwaitingInput`.
 *
 * NOTE: this status is **not** set for the *first* `human()` call of a tool
 * (an upstream assistant-ui race), so it is unreliable as a "a question is
 * pending" signal — use `isPendingToolCallPart` (keyed off `!result`) for
 * that, as the thread-scoped composer detection does.
 *
 * Non-tool parts (text, reasoning, etc.) always return `false`.
 */
export function isAwaitingInputPart(part: { type: string; status?: PartStatusLike; interrupt?: unknown }): boolean {
  const isInterruptStatus = part.status?.type === 'requires-action' && part.status.reason === 'interrupt';
  const hasInterrupt = part.type === 'tool-call' && part.interrupt != null;
  return isInterruptStatus || hasInterrupt;
}
/**
 * Whether a message part is a tool call that has **not yet produced a
 * result** and is not in a terminal error/cancelled state — i.e. a call
 * still in flight, including one blocked on a human-in-the-loop callback
 * (`askUserQuestion` awaiting `human()`).
 *
 * This is the *reliable* "a question is pending" signal.
 * `isAwaitingInputPart` (the `requires-action` / `interrupt` status) is
 * **not** set for the first `human()` call of a tool due to an upstream
 * assistant-ui race — which is exactly why `AskUserQuestionSlides`
 * keys its UI off `!result` rather than the interrupt status. The
 * thread-scoped composer detection must use the same `!result` signal so
 * Enter is intercepted even on the first slide. Mirrors the AI-SDK-layer
 * `pendingToolCalls` (`input-available` / `input-streaming`, no output) at
 * the assistant-ui `ThreadMessage` boundary.
 *
 * Non-tool parts always return `false`.
 */
export function isPendingToolCallPart(part: {
  type: string;
  result?: unknown;
  isError?: boolean;
  status?: PartStatusLike;
}): boolean {
  if (part.type !== 'tool-call') {
    return false;
  }
  if (part.result != null || part.isError === true) {
    return false;
  }
  return !isPartStatusError(part.status) && !isPartStatusCancelled(part.status);
}
